import { faroCoreApiClient } from "@api/faro-core-api-client";
import { AuthContext } from "@context-providers/auth/auth-context";
import { LoginStatus } from "@context-providers/auth/auth-types";
import {
  getUlmUrl,
  isLoginProviderFaro,
} from "@context-providers/auth/auth-utils";
import {
  AuthBroadcastChannel,
  useAuthBroadcastChannel,
} from "@context-providers/auth/use-auth-broadcast-channel";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useLoadingSpinner } from "@context-providers/loading-spinner-provider";
import {
  isUlmRedirectMessage,
  UlmRedirectMessage,
} from "@faro-lotv/gate-keepers";
import { useRouteMatch, isAccountPageRoute } from "@hooks/use-route-match";
import { Dialog } from "@src/flat-ui";
import { runtimeConfig } from "@src/runtime-config";
import { HttpUtils } from "@stellar/utils";
import { resetStore } from "@store/store";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { setLoggedInUserFetchingStatus } from "@store/user/user-slice";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";

/**
 * Component that handles authentication actions with the Unified Login Mask (ULM):
 * - Checks the user login status.
 * - Checks if the user sessions has expired.
 * - Handles login and logout actions.
 * - TODO: Checks if the user has confirmed their email after registration: https://faro01.atlassian.net/browse/ST-2258
 * - TODO: Checks if the user information is complete: https://faro01.atlassian.net/browse/ST-1593.
 */
export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [loginStatus, setLoginStatus] = useState<LoginStatus>("unknown");
  const [shouldShowSessionExpiredDialog, setShouldShowSessionExpiredDialog] =
    useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [auth0LogoutUrl, setAuthLogout0Url] = useState<string | undefined>(
    undefined
  );

  const { setLoadingSpinner } = useLoadingSpinner();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast, handleErrorWithPage } = useErrorContext();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const broadcastChannel = useAuthBroadcastChannel({
    logoutCallback: () => setShouldShowSessionExpiredDialog(true),
  });

  // Check if the current route is an account page (account settings or HBD settings)
  const routeIdentifier = useRouteMatch();
  const isAccountPage = isAccountPageRoute(routeIdentifier);

  /**
   * Checks the login status of the user based on the loggedInUser in the store.
   * The UserLoader component is responsible for fetching the user data at startup.
   * This only reads from the store or makes a lightweight isLoggedIn check as fallback.
   */
  const checkLogin = useCallback(async (): Promise<void> => {
    setLoadingSpinner(true);

    try {
      if (loggedInUser) {
        // User data is already in store from UserLoader
        setLoginStatus("loggedIn");
      } else {
        // If no user in store, make a lightweight API check
        // This handles cases where UserLoader failed silently (401 error for logged-out users)
        await faroCoreApiClient.users.isLoggedIn();
        setLoginStatus("loggedIn");
      }
    } catch (error) {
      // The Core API returns an error with status code 401 if the user is not authenticated
      const errorStatus = HttpUtils.getErrorStatus(error);
      if (
        errorStatus &&
        errorStatus === HttpUtils.HTTP_ERROR_CODE.Unauthorized
      ) {
        setLoginStatus("loggedOut");
      } else {
        handleErrorWithPage({
          id: `checkLoginStatus-${Date.now().toString()}`,
          title:
            "Failed to check the login status of the user. Please reload the page to try again.",
          error,
        });
      }

      // Only stop the loading spinner if the user is not logged in in order to show either the error page or the ULM.
      // If the user is logged in keep the loading spinner because the app will continue to load data.
      setLoadingSpinner(false);
    }
  }, [handleErrorWithPage, loggedInUser, setLoadingSpinner]);

  /**
   * Logs out the user from the application.
   *
   * For users with login method FARO we also need to invalidate the Auth0 session.
   * There is a non-visible iframe where the Auth0 logout will be silently carried out.
   *
   * After a successful logout:
   * - The login status is set as "loggedOut".
   * - The app store state is cleared.
   * - It broadcasts a logout message to any other SDB tab open.
   */
  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    setLoadingSpinner(true);

    try {
      await faroCoreApiClient.users.logoutUser();

      if (isLoginProviderFaro(loggedInUser)) {
        const domain = runtimeConfig.features.auth0.domain;
        const clientId = runtimeConfig.features.auth0.clientId;
        const returnTo = encodeURIComponent(
          runtimeConfig.urls.sphereEntryPageUrl
        );
        const logoutUrl = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
        setAuthLogout0Url(logoutUrl);
      }

      setLoginStatus("loggedOut");
      resetStore(dispatch);
      /**
       * Set fetching status to "succeeded" so the UI shows guest content after
       * logout(e.g., account settings page), not a blank/loading state.
       */
      dispatch(setLoggedInUserFetchingStatus("succeeded"));

      if (broadcastChannel) {
        broadcastChannel.postMessage(
          AuthBroadcastChannel.messages.logoutMessage
        );
      }
    } catch (error) {
      handleErrorWithToast({
        id: `logoutUser-${Date.now().toString()}`,
        title: "Failed to logout. Please try again or reload the page",
        error,
      });
    }

    setIsLoggingOut(false);
    setLoadingSpinner(false);
  }, [
    broadcastChannel,
    dispatch,
    handleErrorWithToast,
    loggedInUser,
    setLoadingSpinner,
  ]);

  /** Checks the login status of the user if the status is unknown */
  useEffect(() => {
    if (loginStatus === "unknown") {
      void checkLogin();
    }
  }, [checkLogin, loginStatus]);

  /**
   * Add event listener for window messages coming from the ULM.
   * Redirect to the received URL if the message is of type UlmRedirectMessage.
   */
  useEffect(() => {
    function onWindowMessage(message: MessageEvent<UlmRedirectMessage>): void {
      // Check `identifier` first to avoid excessive logging of other messages, if the developer
      // has enabled `toggleGuardsLogging(true)`.
      if (
        message?.data?.identifier === "UlmRedirectMessage" &&
        isUlmRedirectMessage(message.data)
      ) {
        window.location.href = message.data.redirectUrl;
      }
    }

    window.addEventListener("message", onWindowMessage);

    return () => window.removeEventListener("message", onWindowMessage);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        loginStatus,
        showSessionExpiredDialog: () => setShouldShowSessionExpiredDialog(true),
        logout,
      }}
    >
      {/* iframe where the Auth0 session invalidation will be silently carried out */}
      <iframe
        title="auth0-logout-iframe"
        style={{ display: "none" }}
        src={auth0LogoutUrl}
      />

      {loginStatus === "loggedOut" && !isAccountPage && (
        <iframe
          title="unified-login-mask"
          id="data-testid-ULM-IFRAME"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            border: "none",
          }}
          src={getUlmUrl()}
        />
      )}

      {/* Allowing account page as the page should be accessible as guest user */}
      {((loginStatus === "loggedIn" && !isLoggingOut) || !!isAccountPage) && (
        <div data-testid="auth-provider">
          {children}
          <Dialog
            open={shouldShowSessionExpiredDialog}
            title="Session expired"
            onConfirm={() => window.location.reload()}
            confirmText="Login"
          >
            Your session has expired. Please login again to continue.
          </Dialog>
        </div>
      )}
    </AuthContext.Provider>
  );
}
